import { Link } from 'react-router-dom';
import {
  PlayCircle,
  ListChecks,
  Award,
  NotebookPen,
  FileText,
  BarChart,
  GraduationCap,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  RefreshCcw,
  Handshake,
  Building2,
  UserCheck,
  FlaskConical,
} from 'lucide-react';
import { Button, Container, Eyebrow, GradientText, IconTile, Reveal, SectionHeading } from '@/components/ui';
import { CtaBand, PageHero } from '@/components/sections';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { CERTIFICATIONS, CERT_VALID_YEARS, COURSES, MAX_PARTNER_DISCOUNT, type Certification } from '@/content/training';
import { INK_U_URL, STATS, orderLink } from '@/lib/site';

function CertificateBadge({ cert, large = false }: { cert: Certification; large?: boolean }) {
  const pro = cert.id === 'professional';
  return (
    <div className={`relative rounded-[var(--radius-glass)] p-6 overflow-hidden ${pro ? 'bg-navy-900 text-white' : 'bg-white border border-glass-border'} ${large ? 'lg:p-8' : ''}`}>
      <div className={`absolute -right-12 -top-12 w-40 h-40 rounded-full blur-2xl ${pro ? 'bg-brand-secondary/40' : 'bg-[#fbbf24]/25'}`} />
      <div className="relative flex items-center gap-4">
        <span
          className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 text-white shadow-lg ${
            pro ? 'bg-gradient-to-br from-brand-primary to-brand-secondary' : 'bg-gradient-to-br from-[#fbbf24] to-[#ca8a04]'
          }`}
        >
          <Award size={26} />
        </span>
        <div>
          <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${pro ? 'text-brand-accent' : 'text-[#a16207]'}`}>Level {pro ? 2 : 1} · {cert.code}</div>
          <div className="text-lg font-bold leading-tight">{cert.name}</div>
        </div>
      </div>
    </div>
  );
}

export function TrainingPage() {
  usePageTitle('Ink U Training');
  const features = [
    { icon: PlayCircle, color: '#7c3aed', title: 'Step-by-step videos', body: `${STATS.videos} short videos across ${STATS.courses} courses, each built around one learning objective.` },
    { icon: ListChecks, color: '#3b82f6', title: 'Graded section quizzes', body: 'A quiz after every section with explanations for each answer and unlimited retakes.' },
    { icon: BarChart, color: '#16a34a', title: 'Progress tracking', body: 'Resume where you left off, with completion percentages per course.' },
    { icon: FileText, color: '#06b6d4', title: 'Transcripts & notes', body: 'Full transcripts and session notes for every video — search them, skim them, print them.' },
    { icon: NotebookPen, color: '#ca8a04', title: 'Private notebook', body: 'Keep your own notes next to each lesson while you learn.' },
    { icon: Award, color: '#dc2626', title: 'Certification', body: 'Two proctored credentials that show customers and employers what you can deliver.' },
  ];
  const associate = COURSES.filter((c) => c.track === 'associate');
  const professional = COURSES.filter((c) => c.track === 'professional');

  return (
    <>
      <PageHero
        dark
        crumbs={[{ label: 'Training' }]}
        eyebrow="Ink U — the Scada.Ink training portal"
        title={
          <>
            Learn it. Prove it. <span className="text-brand-accent">Get certified.</span>
          </>
        }
        intro={`Ink U is a complete, free training portal: ${STATS.courses} video courses from first login to MES, graded quizzes, a built-in practice sandbox, progress tracking and a two-level certification program. Nothing to install and no appliance needed: register with your name, email and company and start in minutes.`}
        actions={
          <>
            <Button href={INK_U_URL} size="lg" variant="light">
              <GraduationCap size={16} /> Start free on Ink U <ArrowUpRight size={15} />
            </Button>
            <Button to="/training/certification" size="lg" variant="outline-light">
              Certification program
            </Button>
          </>
        }
        visual={
          <div className="grid gap-4">
            {CERTIFICATIONS.map((c) => (
              <CertificateBadge key={c.id} cert={c} />
            ))}
            <div className="grid grid-cols-3 gap-3">
              {[
                [String(STATS.courses), 'courses'],
                [STATS.videos, 'videos'],
                ['Free', 'always'],
              ].map(([n, l]) => (
                <div key={l} className="rounded-[var(--radius-glass-sm)] bg-white/5 border border-white/10 p-4 text-center">
                  <div className="text-2xl font-extrabold text-white">{n}</div>
                  <div className="text-xs text-slate-400">{l}</div>
                </div>
              ))}
            </div>
          </div>
        }
      />

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHeading eyebrow="What you get" title="A real training program, not a video playlist" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={i * 60}>
                <div className="glass-card h-full p-6">
                  <IconTile icon={f.icon} color={f.color} />
                  <h3 className="mt-4 font-semibold">{f.title}</h3>
                  <p className="mt-2 text-sm text-text-secondary leading-relaxed">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="pb-20 lg:pb-24">
        <Container>
          <Reveal>
            <div className="glass-panel grid lg:grid-cols-[1.1fr_1fr] gap-8 p-8 lg:p-10 items-center">
              <div>
                <IconTile icon={FlaskConical} color="#7c3aed" />
                <div className="mt-5">
                  <Eyebrow>Built-in sandbox</Eyebrow>
                </div>
                <h2 className="mt-2 text-2xl lg:text-3xl font-bold">
                  Practice on the real software. <GradientText>No appliance, nothing to install.</GradientText>
                </h2>
                <p className="mt-4 text-text-secondary leading-relaxed">
                  Ink U includes a working Scada.Ink sandbox that runs right in your browser, with simulated machines
                  streaming live data. Follow along with each lesson, connect devices, build dashboards, set up alarms and
                  write scripts, all without buying an appliance or installing any software.
                </p>
              </div>
              <ul className="grid gap-3">
                {[
                  ['Unlocks as you learn', 'Features switch on as you progress through the courses, so you only see what the lessons have covered.'],
                  ['Real screens, simulated plant', 'CNC machines, presses and an oven stream data, so trends, alarms and OEE behave like the real thing.'],
                  ['Your work is saved', 'Sign in from any computer and pick up where you left off.'],
                ].map(([title, body]) => (
                  <li key={title} className="flex gap-3 rounded-[var(--radius-glass-sm)] bg-white border border-glass-border p-4">
                    <CheckCircle2 size={18} className="text-brand-primary shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-sm">{title}</div>
                      <div className="text-sm text-text-secondary leading-relaxed">{body}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </Container>
      </section>

      <section id="courses" className="scroll-mt-32 py-20 bg-white border-y border-glass-border">
        <Container>
          <SectionHeading
            eyebrow="Course catalog"
            title={`${STATS.courses} courses in two tracks`}
            intro="The Associate track teaches you to commission an appliance. The Professional track teaches you to run production on it."
            action={
              <Button href={INK_U_URL} variant="secondary">
                Browse on Ink U <ArrowUpRight size={14} />
              </Button>
            }
          />
          <div className="grid lg:grid-cols-2 gap-6">
            {[
              { title: 'Associate track', cert: CERTIFICATIONS[0], courses: associate, color: '#ca8a04' },
              { title: 'Professional track', cert: CERTIFICATIONS[1], courses: professional, color: '#7c3aed' },
            ].map((track) => (
              <div key={track.title} className="glass-panel p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">{track.title}</h3>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${track.color}18`, color: track.color }}>
                    Leads to {track.cert.code}
                  </span>
                </div>
                <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                  {track.courses.map((c) => (
                    <li key={c.number} className="flex items-center gap-3 py-2 border-b border-glass-border text-sm">
                      <span className="w-7 h-7 rounded-md bg-ink-muted text-xs font-bold text-text-secondary flex items-center justify-center shrink-0">{c.number}</span>
                      <span className="text-text-primary">{c.title}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-20">
        <Container>
          <SectionHeading eyebrow="Who it is for" title="Train the whole team" align="center" />
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: UserCheck, title: 'Customers', body: 'Get operators, technicians and engineers productive before the appliance arrives — and keep new hires on the same page.', cta: 'Start learning', href: INK_U_URL },
              { icon: Handshake, title: 'Integrators & resellers', body: `Certify your engineers, win more projects and save up to ${MAX_PARTNER_DISCOUNT}% on every software license you sell.`, cta: 'Partner program', to: '/partners' },
              { icon: Building2, title: 'Training managers', body: 'Proctored exam vouchers can be added to any order, so certification is part of the project plan.', cta: 'Add vouchers to an order', to: orderLink('order') },
            ].map((a) => (
              <div key={a.title} className="glass-card p-6 flex flex-col">
                <a.icon size={22} className="text-brand-primary" />
                <h3 className="mt-4 font-semibold">{a.title}</h3>
                <p className="mt-2 text-sm text-text-secondary leading-relaxed flex-1">{a.body}</p>
                <div className="mt-5">
                  {a.href ? (
                    <Button href={a.href} size="sm" variant="secondary">
                      {a.cta} <ArrowUpRight size={13} />
                    </Button>
                  ) : (
                    <Button to={a.to!} size="sm" variant="secondary" arrow>
                      {a.cta}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <CtaBand title="Put a certified team on your project" body="Order appliances with exam vouchers and commissioning services, or ask us to connect you with a certified partner in your region." />
    </>
  );
}

export function CertificationPage() {
  usePageTitle('Certification Program');
  const steps = [
    { icon: GraduationCap, title: 'Register on Ink U', body: 'Free — full name, email and company.' },
    { icon: PlayCircle, title: 'Complete the track', body: 'Watch every course in the track at your own pace.' },
    { icon: ListChecks, title: 'Pass the quizzes', body: 'Score 80% or better on each section quiz.' },
    { icon: Award, title: 'Pass the exam', body: 'Book a proctored exam with a voucher.' },
  ];
  return (
    <>
      <PageHero
        crumbs={[{ label: 'Training', to: '/training' }, { label: 'Certification' }]}
        eyebrow="Scada.Ink Certification Program"
        title={
          <>
            Two credentials that prove you can <GradientText>deliver</GradientText>
          </>
        }
        intro="Scada.Ink certification is earned on Ink U: complete the track, pass every section quiz, then pass a proctored exam. Hold both credentials and you qualify for the partner program."
        actions={
          <>
            <Button href={INK_U_URL} size="lg">
              <GraduationCap size={16} /> Start the Associate track <ArrowUpRight size={15} />
            </Button>
            <Button to={orderLink('order')} size="lg" variant="secondary">
              Buy exam vouchers
            </Button>
          </>
        }
        visual={
          <div className="grid gap-4 max-w-md mx-auto w-full">
            {CERTIFICATIONS.map((c) => (
              <CertificateBadge key={c.id} cert={c} large />
            ))}
          </div>
        }
      />

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHeading eyebrow="How it works" title="Four steps to certified" align="center" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 80}>
                <div className="glass-card h-full p-6">
                  <div className="flex items-center justify-between">
                    <s.icon size={22} className="text-brand-primary" />
                    <span className="text-3xl font-extrabold text-ink-muted">{i + 1}</span>
                  </div>
                  <h3 className="mt-4 font-semibold">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-text-secondary">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-20 bg-white border-y border-glass-border">
        <Container>
          <div className="grid lg:grid-cols-2 gap-6">
            {CERTIFICATIONS.map((cert) => (
              <div key={cert.id} id={cert.id} className="scroll-mt-32 glass-panel p-7 lg:p-8 flex flex-col">
                <CertificateBadge cert={cert} />
                <p className="mt-6 text-text-secondary leading-relaxed">{cert.summary}</p>
                <div className="mt-2 text-sm">
                  <span className="font-semibold">For: </span>
                  <span className="text-text-secondary">{cert.audience}</span>
                </div>
                <h3 className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-text-muted">Requirements</h3>
                <ul className="mt-3 space-y-2.5">
                  {cert.requirements.map((r) => (
                    <li key={r} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2 size={16} className="text-status-running mt-0.5 shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
                <h3 className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-text-muted">Validates</h3>
                <div className="mt-3 flex flex-wrap gap-1.5 flex-1 content-start">
                  {cert.skills.map((s) => (
                    <span key={s} className="px-2.5 py-1 rounded-md bg-ink-muted text-xs font-medium">
                      {s}
                    </span>
                  ))}
                </div>
                <div className="mt-7">
                  <Button href={INK_U_URL} variant={cert.id === 'professional' ? 'primary' : 'secondary'}>
                    Start the {cert.id === 'professional' ? 'Professional' : 'Associate'} track <ArrowUpRight size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-20">
        <Container>
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="glass-card p-6">
              <Clock size={20} className="text-brand-primary" />
              <h3 className="mt-3 font-semibold">Learn at your own pace</h3>
              <p className="mt-1.5 text-sm text-text-secondary">Courses are self-paced. Exams are booked when you are ready.</p>
            </div>
            <div className="glass-card p-6">
              <RefreshCcw size={20} className="text-brand-primary" />
              <h3 className="mt-3 font-semibold">Current for {CERT_VALID_YEARS} years</h3>
              <p className="mt-1.5 text-sm text-text-secondary">A short recertification exam keeps your credential current as the platform grows.</p>
            </div>
            <div className="glass-card p-6">
              <ListChecks size={20} className="text-brand-primary" />
              <h3 className="mt-3 font-semibold">Unlimited quiz retakes</h3>
              <p className="mt-1.5 text-sm text-text-secondary">Quizzes are for learning — retake them as often as you like. Your latest score counts.</p>
            </div>
          </div>

          <div className="mt-10 relative overflow-hidden rounded-[var(--radius-glass-lg)] bg-navy-900 p-8 lg:p-12 text-white">
            <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-brand-secondary/30 blur-3xl" />
            <div className="relative grid lg:grid-cols-[1.5fr_1fr] gap-8 items-center">
              <div>
                <Eyebrow dark>For resellers & integrators</Eyebrow>
                <h2 className="mt-4 text-3xl font-bold">Both certifications unlock up to {MAX_PARTNER_DISCOUNT}% off the software</h2>
                <p className="mt-3 text-slate-300 leading-relaxed">
                  Partners with Associate and Professional credentials on staff buy Scada.Ink licenses at a discount that grows with sales volume.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Button to="/partners" variant="light" arrow>
                  See partner tiers
                </Button>
                <Button to="/partners#apply" variant="outline-light">
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="pb-20">
        <Container>
          <div className="glass-panel p-6 lg:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div>
              <h3 className="text-lg font-bold">Hiring a Scada.Ink engineer?</h3>
              <p className="mt-1 text-sm text-text-secondary">Ask the candidate for their certification code and we will confirm it is current.</p>
            </div>
            <Link to="/contact" className="text-sm font-semibold text-brand-primary">
              Verify a certification →
            </Link>
          </div>
        </Container>
      </section>
    </>
  );
}
